import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound, LockKeyhole, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../components/layouts";
import { Button, Notice, SubmitButton } from "../components/ui";
import { useApp } from "../context/AppContext";
import { ClientApiError, request } from "../lib/api";

declare global {
  interface Window {
    onPaymeQRTurnstile: ((token: string) => void) | undefined;
  }
}

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const { t, user, refreshSession } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const turnstile = useTurnstile(mode === "register");

  if (user !== null) return <Navigate to="/dashboard" replace />;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError(""); setNeedsVerification(false);
    try {
      const headers = turnstile.token === "" ? {} : { "X-Turnstile-Token": turnstile.token };
      if (mode === "login") {
        await request("/api/auth/sign-in/email", { method: "POST", body: JSON.stringify({ email, password }), headers });
        await refreshSession();
        void navigate("/dashboard");
      } else {
        const result = await request<{ token?: string | null }>("/api/auth/sign-up/email", { method: "POST", body: JSON.stringify({ name, email, password }), headers });
        if (result.token !== null && result.token !== undefined) {
          await refreshSession();
          void navigate("/dashboard");
        } else {
          void navigate(`/verify-email?email=${encodeURIComponent(email.trim().toLowerCase())}&sent=1`);
        }
      }
    } catch (reason) {
      const code = authErrorCode(reason);
      if (mode === "login" && code === "EMAIL_NOT_VERIFIED") setNeedsVerification(true);
      setError(authErrorMessage(reason));
    } finally { setBusy(false); }
  }

  return <AuthShell eyebrow={mode === "register" ? "NEW ACCOUNT" : "WELCOME BACK"} title={mode === "register" ? t.auth.register : t.auth.login} description={mode === "register" ? "สร้างบัญชีแล้วกรอกรหัส 6 หลักที่ส่งไปยังอีเมล" : "จัดการ API และ live donation workspace ของคุณ"}>
    {searchParams.get("verified") === "1" && <Notice tone="success"><strong>ยืนยันอีเมลสำเร็จ</strong><br />เข้าสู่ระบบด้วยบัญชีที่เพิ่งยืนยันได้เลย</Notice>}
    <form onSubmit={(event) => { void submit(event); }} noValidate>
      {mode === "register" && <label><span>{t.auth.name}</span><input autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} required minLength={2} /></label>}
      <label><span>{t.auth.email}</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
      <label><span>{t.auth.password}</span><div className="password-field"><input type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} maxLength={128} aria-describedby={mode === "register" ? "password-hint" : undefined} /><Button type="button" variant="ghost" aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</Button></div>{mode === "register" && <small id="password-hint">อย่างน้อย 8 ตัวอักษร และไม่ควรซ้ำกับรหัสผ่านบริการอื่น</small>}</label>
      {turnstile.siteKey !== "" && mode === "register" && <div className="cf-turnstile" data-sitekey={turnstile.siteKey} data-callback="onPaymeQRTurnstile" data-theme="auto" />}
      {error !== "" && <p className="form-error" role="alert">{error}</p>}
      {needsVerification && <Link className="inline-action" to={`/verify-email?email=${encodeURIComponent(email)}`}><Mail />ส่งรหัสยืนยันอีเมลใหม่</Link>}
      <SubmitButton busy={busy}>{mode === "login" ? t.auth.submitLogin : t.auth.submitRegister}<ArrowRight size={17} /></SubmitButton>
    </form>
    <div className="auth-links">{mode === "login" ? <><Link to="/forgot-password">{t.auth.forgot}</Link><span>{t.auth.noAccount} <Link to="/register">{t.auth.register}</Link></span></> : <span>{t.auth.haveAccount} <Link to="/login">{t.auth.login}</Link></span>}</div>
  </AuthShell>;
}

export function VerifyEmailPage() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(searchParams.get("sent") === "1" ? "ส่งรหัสยืนยันไปยังอีเมลแล้ว" : "");
  const [cooldown, setCooldown] = useState(searchParams.get("sent") === "1" ? 60 : 0);
  const turnstile = useTurnstile(true);
  useCountdown(cooldown, setCooldown);

  if (user !== null) return <Navigate to="/dashboard" replace />;

  async function verify(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setError("กรุณากรอกรหัสตัวเลข 6 หลัก"); return; }
    setBusy(true); setError(""); setMessage("");
    try {
      await request("/api/auth/email-otp/verify-email", { method: "POST", body: JSON.stringify({ email, otp }) });
      void navigate(`/login?verified=1&email=${encodeURIComponent(email)}`, { replace: true });
    } catch (reason) { setError(authErrorMessage(reason)); }
    finally { setBusy(false); }
  }

  async function resend() {
    setResendBusy(true); setError(""); setMessage("");
    try {
      const headers = turnstile.token === "" ? {} : { "X-Turnstile-Token": turnstile.token };
      await request("/api/auth/email-otp/send-verification-otp", { method: "POST", body: JSON.stringify({ email, type: "email-verification" }), headers });
      setMessage("หากอีเมลนี้มีบัญชี ระบบได้ส่งรหัสใหม่แล้ว"); setCooldown(60);
    } catch (reason) { setError(authErrorMessage(reason)); }
    finally { setResendBusy(false); }
  }

  return <AuthShell eyebrow="EMAIL VERIFICATION" title="ยืนยันอีเมล" description="กรอกรหัสตัวเลข 6 หลัก รหัสใช้ได้ครั้งเดียวและหมดอายุใน 10 นาที">
    <div className="auth-steps" aria-label="ขั้นตอนสมัครบัญชี"><span className="done"><CheckCircle2 />สร้างบัญชี</span><i /><span className="active"><KeyRound />ยืนยันอีเมล</span></div>
    {message !== "" && <Notice tone="success"><span role="status">{message}</span></Notice>}
    <form onSubmit={(event) => { void verify(event); }} noValidate>
      <label><span>อีเมล</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
      <label><span>รหัสยืนยัน 6 หลัก</span><input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" aria-describedby="otp-hint" required /><small id="otp-hint">ตรวจสอบ Inbox และ Spam ของ {email || "อีเมลที่สมัคร"}</small></label>
      {turnstile.siteKey !== "" && <div className="cf-turnstile" data-sitekey={turnstile.siteKey} data-callback="onPaymeQRTurnstile" data-theme="auto" />}
      {error !== "" && <p className="form-error" role="alert">{error}</p>}
      <SubmitButton busy={busy}>ยืนยันและไปหน้าเข้าสู่ระบบ<ArrowRight size={17} /></SubmitButton>
    </form>
    <div className="auth-secondary-actions"><Button variant="ghost" disabled={resendBusy || cooldown > 0 || email === ""} onClick={() => { void resend(); }}><RefreshCw />{cooldown > 0 ? `ส่งใหม่ได้ใน ${cooldown} วินาที` : resendBusy ? "กำลังส่ง…" : "ส่งรหัสใหม่"}</Button><Link to="/register"><ArrowLeft />กลับไปสมัครบัญชี</Link></div>
  </AuthShell>;
}

export function PasswordRecoveryPage() {
  const { user } = useApp();
  const [step, setStep] = useState<"request" | "reset" | "done">("request");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const turnstile = useTurnstile(true);
  useCountdown(cooldown, setCooldown);

  if (user !== null) return <Navigate to="/dashboard" replace />;

  async function requestCode(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setBusy(true); setError("");
    try {
      const headers = turnstile.token === "" ? {} : { "X-Turnstile-Token": turnstile.token };
      await request("/api/auth/email-otp/request-password-reset", { method: "POST", body: JSON.stringify({ email }), headers });
      setStep("reset"); setCooldown(60);
    } catch (reason) { setError(authErrorMessage(reason)); }
    finally { setBusy(false); }
  }

  async function resetPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    if (!/^\d{6}$/.test(otp)) { setError("กรุณากรอกรหัสตัวเลข 6 หลัก"); return; }
    if (password.length < 8) { setError("รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร"); return; }
    if (password !== confirmation) { setError("รหัสผ่านใหม่และการยืนยันไม่ตรงกัน"); return; }
    setBusy(true);
    try {
      await request("/api/auth/email-otp/reset-password", { method: "POST", body: JSON.stringify({ email, otp, password }) });
      setStep("done"); setOtp(""); setPassword(""); setConfirmation("");
    } catch (reason) { setError(authErrorMessage(reason)); }
    finally { setBusy(false); }
  }

  return <AuthShell eyebrow="ACCOUNT RECOVERY" title={step === "done" ? "ตั้งรหัสผ่านใหม่แล้ว" : "กู้รหัสผ่าน"} description={step === "request" ? "รับรหัส 6 หลักทางอีเมลเพื่อยืนยันว่าเป็นเจ้าของบัญชี" : step === "reset" ? "กรอกรหัสจากอีเมลและตั้งรหัสผ่านใหม่" : "session เดิมของบัญชีถูกยกเลิกเพื่อความปลอดภัย"}>
    <div className="auth-steps" aria-label="ขั้นตอนกู้รหัสผ่าน"><span className={step !== "request" ? "done" : "active"}><Mail />รับรหัส</span><i /><span className={step === "done" ? "done" : step === "reset" ? "active" : ""}><KeyRound />ตั้งรหัสใหม่</span></div>
    {step === "done" ? <><Notice tone="success"><strong>เปลี่ยนรหัสผ่านสำเร็จ</strong><br />กรุณาเข้าสู่ระบบอีกครั้งด้วยรหัสผ่านใหม่</Notice><Link className="button button-primary auth-full-action" to={`/login?email=${encodeURIComponent(email)}`}>ไปหน้าเข้าสู่ระบบ<ArrowRight /></Link></> : step === "request" ? <form onSubmit={(event) => { void requestCode(event); }} noValidate><label><span>อีเมลของบัญชี</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>{turnstile.siteKey !== "" && <div className="cf-turnstile" data-sitekey={turnstile.siteKey} data-callback="onPaymeQRTurnstile" data-theme="auto" />}{error !== "" && <p className="form-error" role="alert">{error}</p>}<SubmitButton busy={busy}>ส่งรหัสกู้รหัสผ่าน<ArrowRight /></SubmitButton></form> : <form onSubmit={(event) => { void resetPassword(event); }} noValidate><Notice tone="success">หาก <strong>{email}</strong> มีบัญชี ระบบได้ส่งรหัสแล้ว</Notice><label><span>รหัสกู้รหัสผ่าน 6 หลัก</span><input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" required /></label><label><span>รหัสผ่านใหม่</span><div className="password-field"><input type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} maxLength={128} required /><Button type="button" variant="ghost" aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff /> : <Eye />}</Button></div></label><label><span>ยืนยันรหัสผ่านใหม่</span><input type={showPassword ? "text" : "password"} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={8} maxLength={128} required /></label>{error !== "" && <p className="form-error" role="alert">{error}</p>}<SubmitButton busy={busy}>ตั้งรหัสผ่านใหม่<ArrowRight /></SubmitButton><div className="auth-secondary-actions"><Button type="button" variant="ghost" disabled={busy || cooldown > 0} onClick={() => { void requestCode(); }}><RefreshCw />{cooldown > 0 ? `ส่งใหม่ได้ใน ${cooldown} วินาที` : "ส่งรหัสใหม่"}</Button><Button type="button" variant="ghost" onClick={() => { setStep("request"); setError(""); }}>เปลี่ยนอีเมล</Button></div></form>}
    <div className="auth-links"><Link to="/login"><ArrowLeft />กลับไปหน้าเข้าสู่ระบบ</Link></div>
  </AuthShell>;
}

function AuthShell({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: string; children: ReactNode }) {
  return <main className="auth-page"><section className="auth-story"><Brand /><div><p className="eyebrow">PAYMENT INFRASTRUCTURE</p><h1>สถานะที่ตรงไปตรงมา<br />ตั้งแต่ QR ถึง Live</h1><p>สร้าง API key แบบเปิดเผยครั้งเดียว เก็บ PromptPay identifier แบบเข้ารหัส และแยกการสร้าง QR ออกจากการยืนยันเงิน</p></div><ul><li><ShieldCheck />Server-side workspace isolation</li><li><LockKeyhole />Secure HTTP-only sessions</li><li><Mail />6-digit email verification</li></ul><small>paymeQR does not hold customer funds.</small></section><section className="auth-form-wrap"><div className="auth-panel"><header className="auth-form-head"><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{description}</p></header>{children}</div></section></main>;
}

function useTurnstile(enabled: boolean): { token: string; siteKey: string } {
  const [token, setToken] = useState("");
  const siteKey = enabled ? (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "" : "";
  useEffect(() => {
    if (!enabled || siteKey === "") return;
    window.onPaymeQRTurnstile = setToken;
    if (document.querySelector('script[data-paymeqr-turnstile]') === null) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true; script.defer = true; script.dataset.paymeqrTurnstile = "true";
      document.head.appendChild(script);
    }
    return () => { window.onPaymeQRTurnstile = undefined; };
  }, [enabled, siteKey]);
  return { token, siteKey };
}

function useCountdown(value: number, setValue: React.Dispatch<React.SetStateAction<number>>): void {
  useEffect(() => {
    if (value <= 0) return;
    const timer = window.setTimeout(() => setValue((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [setValue, value]);
}

function authErrorCode(reason: unknown): string {
  if (!(reason instanceof ClientApiError)) return "";
  return reason.body.error?.code ?? reason.body.code ?? "";
}

function authErrorMessage(reason: unknown): string {
  const code = authErrorCode(reason);
  if (code === "EMAIL_NOT_VERIFIED") return "อีเมลนี้ยังไม่ได้ยืนยัน กรุณากรอกรหัส 6 หลักก่อนเข้าสู่ระบบ";
  if (code === "INVALID_OTP") return "รหัสไม่ถูกต้อง กรุณาตรวจสอบแล้วลองอีกครั้ง";
  if (code === "OTP_EXPIRED") return "รหัสหมดอายุแล้ว กรุณาขอรหัสใหม่";
  if (code === "TOO_MANY_ATTEMPTS") return "กรอกรหัสผิดหลายครั้งเกินไป กรุณาขอรหัสใหม่";
  if (code === "USER_ALREADY_EXISTS") return "อีเมลนี้มีบัญชีอยู่แล้ว กรุณาเข้าสู่ระบบหรือกู้รหัสผ่าน";
  if (code === "INVALID_EMAIL_OR_PASSWORD") return "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
  if (code === "PASSWORD_TOO_SHORT") return "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร";
  if (reason instanceof ClientApiError && reason.message !== "") return reason.message;
  return "ไม่สามารถดำเนินการได้ กรุณาลองอีกครั้ง";
}
